import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, TextField, Button, Paper, CircularProgress, Alert, Snackbar, Fade } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Percent, Sparkles, TrendingDown, Zap } from 'lucide-react';

const UNIT_PRICE_FALLBACK = 40;
const FALLBACK_TIERS = [
  { quantidade: 1,   valor: 40,    valorBruto: 40,    desconto: 0,  economia: 0,    valorPorToken: 40 },
  { quantidade: 3,   valor: 108,   valorBruto: 120,   desconto: 10, economia: 12,   valorPorToken: 36 },
  { quantidade: 5,   valor: 170,   valorBruto: 200,   desconto: 15, economia: 30,   valorPorToken: 34 },
  { quantidade: 10,  valor: 320,   valorBruto: 400,   desconto: 20, economia: 80,   valorPorToken: 32 },
  { quantidade: 25,  valor: 750,   valorBruto: 1000,  desconto: 25, economia: 250,  valorPorToken: 30 },
  { quantidade: 50,  valor: 1440,  valorBruto: 2000,  desconto: 28, economia: 560,  valorPorToken: 28.8 },
  { quantidade: 100, valor: 2720,  valorBruto: 4000,  desconto: 32, economia: 1280, valorPorToken: 27.2 },
];

const BEST_SELLER_QTY = 5;
const BEST_VALUE_QTY = 25;

const fmtBRL = (n) => 'R$ ' + Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function localCalc(qty, unitPrice) {
  const q = Math.floor(Number(qty));
  if (!Number.isFinite(q) || q < 1) return { quantidade: 0, valor: 0, valorBruto: 0, desconto: 0, economia: 0, valorPorToken: 0 };
  let desconto = 0;
  if (q >= 500) desconto = 35;
  else if (q >= 100) desconto = 32;
  else if (q >= 50) desconto = 28;
  else if (q >= 25) desconto = 25;
  else if (q >= 10) desconto = 20;
  else if (q >= 5) desconto = 15;
  else if (q >= 3) desconto = 10;
  const valorBruto = q * unitPrice;
  const valor = parseFloat((valorBruto * (1 - desconto / 100)).toFixed(2));
  const economia = parseFloat((valorBruto - valor).toFixed(2));
  const valorPorToken = parseFloat((valor / q).toFixed(2));
  return { quantidade: q, valor, valorBruto, desconto, economia, valorPorToken };
}

export default function ComprarTokens() {
  const [quantidade, setQuantidade] = useState(1);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [success, setSuccess] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const [hoveredTier, setHoveredTier] = useState(null);

  const [unitPrice, setUnitPrice] = useState(UNIT_PRICE_FALLBACK);
  const [tiers, setTiers] = useState(FALLBACK_TIERS);

  useEffect(() => {
    let alive = true;
    const token = localStorage.getItem('token');
    fetch(`${process.env.REACT_APP_API_URL}/api/pagamento/pricing`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!alive || !data?.success) return;
        if (Array.isArray(data.featuredTiers) && data.featuredTiers.length) {
          setTiers(data.featuredTiers);
        }
        if (data.unitPrice) setUnitPrice(data.unitPrice);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const preco = useMemo(() => {
    const found = tiers.find(t => t.quantidade === Number(quantidade));
    return found || localCalc(quantidade, unitPrice);
  }, [quantidade, tiers, unitPrice]);

  // Sugestão de upsell: se quantidade=1, sugere subir pro tier 3 (R$12 a mais leva 2 tokens extras na prática? não — 3 com 10% = R$108 = R$68 a mais. Sugere 5 que tem o melhor degrau psicológico)
  const upsellHint = useMemo(() => {
    if (Number(quantidade) >= 3) return null;
    const tier5 = tiers.find(t => t.quantidade === BEST_SELLER_QTY);
    if (!tier5) return null;
    const economiaPorToken = unitPrice - tier5.valorPorToken;
    return {
      msg: `💡 Levando ${BEST_SELLER_QTY} tokens você economiza ${fmtBRL(economiaPorToken)} por token`,
      qty: BEST_SELLER_QTY,
      total: tier5.valor,
    };
  }, [quantidade, tiers, unitPrice]);

  const handleComprar = async () => {
    setErro('');
    setSuccess('');
    setSnackbar({ open: false, message: '', severity: 'success' });

    if (quantidade < 1) {
      setErro('A quantidade mínima é 1 token (R$40,00)');
      setSnackbar({ open: true, message: 'A quantidade mínima é 1 token (R$40,00)', severity: 'error' });
      return;
    }

    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        value: preco.valor, currency: 'BRL', content_name: 'Tokens Painel FTM',
        content_category: 'Tokens', content_ids: ['token-ftm'], num_items: Math.floor(quantidade),
      });
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pagamento/pix`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ quantidadeTokens: Math.floor(quantidade) })
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Pix gerado! Abrindo checkout...');
        setSnackbar({ open: true, message: 'Pix gerado! Abrindo checkout...', severity: 'success' });
        navigate(`/checkout/${data.order_id}`);
      } else {
        setErro(data.error || 'Erro ao gerar cobrança Pix');
        setSnackbar({ open: true, message: data.error || 'Erro ao gerar cobrança Pix', severity: 'error' });
      }
    } catch (err) {
      setErro('Erro ao conectar com o servidor');
      setSnackbar({ open: true, message: 'Erro ao conectar com o servidor', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 520, mx: 'auto', mt: { xs: 2, sm: 6 }, px: { xs: 2, sm: 0 } }}>
        <Button startIcon={<ArrowLeft size={20} strokeWidth={1.75} />} onClick={() => navigate('/dashboard')} sx={{ mb: 2, color: '#7C3AED', fontWeight: 700, textTransform: 'none', background: 'none', boxShadow: 'none', '&:hover': { background: 'rgba(124,58,237,0.08)' } }}>
          Voltar para o Dashboard
        </Button>
        <Paper sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 5, background: 'rgba(30,32,40,0.98)', boxShadow: '0 8px 32px 0 rgba(124,58,237,0.15)' }}>
          <Typography variant="h4" sx={{ color: '#7C3AED', fontWeight: 800, mb: 1, textAlign: 'center', letterSpacing: 1 }}>
            Comprar Tokens
          </Typography>
          <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 3, textAlign: 'center' }}>
            <b>1 token = 1 site.</b> Quanto mais comprar, maior o desconto.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.2, mb: 2.5 }}>
            {tiers.map((t) => {
              const qty = t.quantidade;
              const isSelected = Number(quantidade) === qty;
              const isHovered = hoveredTier === qty;
              const isBestSeller = qty === BEST_SELLER_QTY;
              const isBestValue = qty === BEST_VALUE_QTY;
              return (
                <Box
                  key={qty}
                  onClick={() => setQuantidade(qty)}
                  onMouseEnter={() => setHoveredTier(qty)}
                  onMouseLeave={() => setHoveredTier(null)}
                  sx={{
                    position: 'relative', cursor: 'pointer', textAlign: 'center',
                    py: 1.6, px: 1, borderRadius: 3, overflow: 'visible',
                    transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                    transform: isHovered ? 'translateY(-4px) scale(1.04)' : 'translateY(0) scale(1)',
                    border: isSelected
                      ? '2px solid #7C3AED'
                      : isBestSeller
                        ? '1.5px solid rgba(245, 158, 11, 0.6)'
                        : '1px solid rgba(124,58,237,0.3)',
                    background: isSelected
                      ? 'rgba(124,58,237,0.18)'
                      : isBestSeller
                        ? 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(124,58,237,0.08))'
                        : 'rgba(20,25,38,0.8)',
                    boxShadow: isHovered ? '0 8px 24px rgba(124,58,237,0.3)' : (isBestSeller ? '0 4px 16px rgba(245,158,11,0.15)' : 'none'),
                    '&:hover': { borderColor: '#7C3AED' },
                    mt: (isBestSeller || isBestValue) ? 1.6 : 0,
                  }}
                >
                  {isBestSeller && (
                    <Box sx={{
                      position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                      background: 'linear-gradient(90deg, #F59E0B, #F97316)', borderRadius: 2,
                      px: 0.9, py: 0.25, display: 'flex', alignItems: 'center', gap: 0.3,
                      boxShadow: '0 3px 8px rgba(245,158,11,0.4)', whiteSpace: 'nowrap',
                    }}>
                      <Sparkles size={10} color="#fff" />
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: '#fff', letterSpacing: 0.3 }}>
                        MAIS VENDIDO
                      </Typography>
                    </Box>
                  )}
                  {isBestValue && !isBestSeller && (
                    <Box sx={{
                      position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                      background: 'linear-gradient(90deg, #10B981, #059669)', borderRadius: 2,
                      px: 0.9, py: 0.25, display: 'flex', alignItems: 'center', gap: 0.3,
                      whiteSpace: 'nowrap',
                    }}>
                      <TrendingDown size={10} color="#fff" />
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: '#fff', letterSpacing: 0.3 }}>
                        MELHOR PREÇO
                      </Typography>
                    </Box>
                  )}
                  <Typography sx={{ fontWeight: 900, fontSize: '1.4rem', lineHeight: 1, color: isSelected ? '#A78BFA' : '#fff' }}>
                    {qty}
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: '#9CA3AF', mt: 0.3, fontWeight: 600 }}>
                    {qty === 1 ? 'token' : 'tokens'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: isSelected ? '#fff' : '#D1D5DB', mt: 0.6 }}>
                    {fmtBRL(t.valor)}
                  </Typography>
                  {t.desconto > 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.2, mt: 0.4 }}>
                      <Percent size={9} color="#10B981" />
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#10B981' }}>
                        -{t.desconto}%
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: '0.62rem', color: 'transparent', mt: 0.4 }}>—</Typography>
                  )}
                  <Typography sx={{ fontSize: '0.58rem', color: '#6B7280', mt: 0.2 }}>
                    {fmtBRL(t.valorPorToken)}/un
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <TextField
            label="Ou digite uma quantidade"
            type="number"
            value={quantidade}
            onChange={e => setQuantidade(Number(e.target.value))}
            fullWidth
            inputProps={{ min: 1 }}
            sx={{ mb: 2 }}
          />

          {upsellHint && (
            <Box
              onClick={() => setQuantidade(upsellHint.qty)}
              sx={{
                cursor: 'pointer', mb: 2, p: 1.5, borderRadius: 2,
                background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(124,58,237,0.08))',
                border: '1px dashed rgba(245,158,11,0.5)',
                display: 'flex', alignItems: 'center', gap: 1,
                transition: 'all 0.2s ease',
                '&:hover': { background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(124,58,237,0.15))', borderColor: 'rgba(245,158,11,0.8)' }
              }}
            >
              <Zap size={18} color="#F59E0B" />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: '#F59E0B', fontWeight: 700, fontSize: '0.85rem' }}>
                  {upsellHint.msg}
                </Typography>
                <Typography sx={{ color: '#9CA3AF', fontSize: '0.72rem' }}>
                  Toque pra trocar pro pacote de {upsellHint.qty} tokens ({fmtBRL(upsellHint.total)})
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ mb: 3, p: 2, borderRadius: 2, background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.2)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                Valor total:
              </Typography>
              <Box sx={{ textAlign: 'right' }}>
                {preco.desconto > 0 && (
                  <Typography sx={{ color: '#6B7280', fontSize: '0.8rem', textDecoration: 'line-through', lineHeight: 1 }}>
                    {fmtBRL(preco.valorBruto)}
                  </Typography>
                )}
                <Typography variant="h5" sx={{ color: '#7C3AED', fontWeight: 900, letterSpacing: 1 }}>
                  {fmtBRL(preco.valor)}
                </Typography>
              </Box>
            </Box>
            {preco.desconto > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 2, px: 1.2, py: 0.3 }}>
                  <Percent size={12} color="#10B981" />
                  <Typography sx={{ color: '#10B981', fontWeight: 700, fontSize: '0.78rem' }}>
                    {preco.desconto}% off — economia de {fmtBRL(preco.economia)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            onClick={handleComprar}
            disabled={loading}
            sx={{ fontWeight: 700, mb: 2, py: 1.5, fontSize: 18, borderRadius: 3 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Ir para Checkout'}
          </Button>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Paper>
      </Box>
    </Fade>
  );
}
